import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import IMG_HR from 'assets/slickpay-splash/time-off/hr.svg';
import IMG_PROFILE_1 from 'assets/slickpay-splash/time-off/profile-1.svg';
import IMG_PROFILE_2 from 'assets/slickpay-splash/time-off/profile-2.svg';
import IMG_PROFILE_3 from 'assets/slickpay-splash/time-off/profile-3.svg';
import IMG_PROFILE_4 from 'assets/slickpay-splash/time-off/profile-4.svg';
import IMG_QUOTE from 'assets/slickpay-splash/time-off/quote-mark.svg';
import IMG_LOGO from 'assets/slickpay-splash/time-off/slick-hr-logo.png';
import IMG_AVATAR from 'assets/slickpay-splash/time-off/testimonial-avatar.svg';
import ExternalLink from 'components/generic/ExternalLink.component';
import FlareStar from 'components/generic/flare/FlareStar.component';
import TimeOffSplashBoxes from 'components/hr/timeOffSplash/TimeOffSplashBoxes.component';
import PlanBenefitsSplashPanel from 'components/hr/timeOffSplash/TimeOffSplashPlanBenefitsPanel.component';
import TimeOffSplashStaff from 'components/hr/timeOffSplash/TimeOffSplashStaff.component';
import TimeOffSplashTabsContainer from 'components/hr/timeOffSplash/TimeOffSplashTabsContainer.component';
import UpgradePlanButton from 'components/hr/timeOffSplash/UpgradePlanButton.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import S from 'constants/string.const';
import useWindowScroll from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCalendarMode, selectCurrentSalonId } from 'selectors/currentSalon.selectors';

import PadLock from 'lib/components/PadLock';

import CN from './TimeOffSplashPage.module.scss';

const LINK = 'https://slick.webinargeek.com/slick-ed-episode-8-how-to-build-a-winning-team-top-tips-from-successful-managers/join/w8eldcdp';

const TimeOffSplashPage = ({ isBrightMode, isDarkMode, isLightMode }) => {
  const { t } = useTranslation();

  useWindowScroll(0, 0);

  return (
    <div
      data-bem="TimeOffSplashPage"
      className={cn({
        [CN.layout]: true,
        [CN.dark]: isDarkMode,
        'light-class': isLightMode,
        'dark-class': isDarkMode,
        'bright-class': isBrightMode,
      })}
    >
      <header data-bem="TimeOffSplashPage__header" className={CN.header}>
        <SidebarMenu />
        <div className={CN.globalNav}>
          <div className={cn(CN.pageName, CN.timeOff)} />
          <nav className={CN.menuList}>
            <ul className={CN.ulScrollMenu} />
          </nav>
        </div>
        <PadLock inMenu />
      </header>

      <div data-bem="TimeOffSplashPage__topContent" className={CN.topContent}>
        <div className={CN.column}>
          <div className={CN.left}>
            <div className={CN.logo}>
              <img src={IMG_LOGO} alt="Slick HR logo" />
            </div>
            <h3 className={CN.title}>
              {t('humanResources.splashOverview.titlePart1')} <br />
              {t('humanResources.splashOverview.titlePart2')}
            </h3>

            <div className={CN.texEmoji}>
              <span className={CN.emoji} role="img" aria-labelledby="Star">
                {S.star}
              </span>
              {S.space}
              {t('humanResources.splashOverview.headerBullet1')}
            </div>
            <div className={CN.texEmoji}>
              <span className={CN.emoji} role="img" aria-labelledby="Star">
                {S.star}
              </span>
              {S.space}
              {t('humanResources.splashOverview.headerBullet2')}
            </div>
            <div className={CN.texEmoji}>
              <span className={CN.emoji} role="img" aria-labelledby="Star">
                {S.star}
              </span>
              {S.space}
              {t('humanResources.splashOverview.headerBullet3')}
            </div>
            <div className={CN.texEmoji}>
              <UpgradePlanButton text={t('humanResources.splashOverview.upgradeButton')} />
            </div>
          </div>
          <div className={CN.right}>
            <div className={CN.staffList}>
              <TimeOffSplashStaff finished={100} color="splash" src={IMG_PROFILE_1} isCompleted />
              <TimeOffSplashStaff finished={68} color="splash" src={IMG_PROFILE_2} />
              <TimeOffSplashStaff finished={50} color="alert" src={IMG_PROFILE_3} />
              <TimeOffSplashStaff finished={30} color="warn" src={IMG_PROFILE_4} />
              <img src={IMG_HR} alt="HR hero" />
            </div>
          </div>
        </div>
      </div>

      {/* Do less, Grow more Section*/}
      <TimeOffSplashTabsContainer />
      {/* Slick Tips & Advice Section*/}
      <TimeOffSplashBoxes />

      <div data-bem="Banner" className={CN.banner}>
        <div className={CN.bannerContainer}>
          <div className={CN.logo} />
          <div className={CN.text}>{t('humanResources.splashOverview.watchWebinarText')}</div>
          <ExternalLink link={LINK} variant="inline">
            {t('humanResources.splashOverview.watchNowLink')}
          </ExternalLink>
        </div>
      </div>

      <div className={CN.twoColumns}>
        <PlanBenefitsSplashPanel />
        <div className={CN.testimonial}>
          <div className={CN.imageContainer}>
            <img src={IMG_AVATAR} alt="HR avatar" className={CN.avatar} />
            <img src={IMG_QUOTE} alt="HR avatar" className={CN.quote} />
          </div>
          <div className={CN.text}>{t('humanResources.splashOverview.testimonialText')}</div>
          <FlareStar top="50" right="58" animation="blur" />
          <FlareStar bottom="30" left="44" size="20" animation="pulse" />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  calendarMode: selectCurrentSalonCalendarMode(state),
  salonId: selectCurrentSalonId(state),
});

const TimeOffSplashPageHoc = connect(mapStateToProps)(withTheme()(TimeOffSplashPage));
export default TimeOffSplashPageHoc;
